import { useCallback, useEffect, useState } from 'react';

/**
 * Whole seconds left until a deadline, updated once a second.
 *
 * Measured against the clock rather than decremented per tick: the director
 * leaves for their messages app to read the code, and a backgrounded tab's
 * timers are throttled or paused, so a per-tick count would come back showing
 * most of a wait that has already passed.
 */
export const useCountdown = (): { secondsLeft: number; start: (seconds: number) => void } => {
  const [deadline, setDeadline] = useState<number | null>(null);
  const [now, setNow] = useState(0);

  useEffect(() => {
    if (deadline === null) return;

    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= deadline) setDeadline(null);
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [deadline]);

  const start = useCallback((seconds: number): void => {
    const current = Date.now();
    setNow(current);
    setDeadline(current + seconds * 1000);
  }, []);

  const secondsLeft = deadline === null ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000));

  return { secondsLeft, start };
};
